import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { format, isBefore } from 'date-fns';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getTaskTimeUnits } from '../common/taskProgress';
import MarkdownContent from '../common/MarkdownContent';

const {
  FiEdit2, FiTrash2, FiCheckCircle, FiCircle, FiCalendar, FiFlag,
  FiList, FiTag, FiClock, FiArchive, FiInfo, FiFileText
} = FiIcons;

const TaskItem = ({ task, onToggleComplete, onEdit, onDelete, onArchive, project, tags = [] }) => {
  const { t } = useTranslation();
  const [showAllSubtasks, setShowAllSubtasks] = useState(false);
  const [showNotes, setShowNotes] = useState(true);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-500 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'medium':
        return 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'low':
        return 'text-green-500 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      default:
        return 'text-slate-500 bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours > 0 ? `${hours}h ` : ''}${mins > 0 ? `${mins}m` : ''}`;
  };

  const isOverdue = () => {
    if (!task.dueDate || task.completed) return false;
    const now = new Date();
    const dueDate = new Date(task.dueDate);
    return isBefore(dueDate, now);
  };

  const isCompleted = task.completed;
  const taskIsOverdue = isOverdue();
  const projectColor = project?.color || '#3b82f6';
  
  // Get task tags
  const taskTags = task.tags?.map(tagId => tags.find(t => t.id === tagId)).filter(Boolean) || [];
  const subtasks = task.subTasks || [];
  const visibleSubtasks = showAllSubtasks ? subtasks : subtasks.slice(0, 3);
  const hiddenSubtasksCount = Math.max(0, subtasks.length - visibleSubtasks.length);
  const taskProgress = getTaskTimeUnits(task);
  const progressToneClass = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500'
  };
  const progressSoftToneClass = {
    green: 'bg-green-100 dark:bg-green-900/30',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30',
    red: 'bg-red-100 dark:bg-red-900/30'
  };
  const progressTextClass = {
    green: 'text-green-600 dark:text-green-400',
    yellow: 'text-yellow-600 dark:text-yellow-400',
    red: 'text-red-600 dark:text-red-400'
  };
  const tuLabel = taskProgress
    ? t(`tasks.timeUnits${taskProgress.tuStatus}`, {
      score: Math.abs(taskProgress.tuScore)
    })
    : '';
  const tuSignedLabel = taskProgress?.tuScore > 0
    ? `+${taskProgress.tuScore} TU`
    : `${taskProgress?.tuScore || 0} TU`;
  const tuTooltip = taskProgress
    ? t('tasks.timeUnitsTooltip', {
      score: taskProgress.tuScore
    })
    : '';

  return (
    <motion.div
      id={`task-${task.id}`}
      layout
      className={`bg-white dark:bg-slate-800 rounded-xl p-6 shadow-soft border border-slate-200 dark:border-slate-700 hover:shadow-medium transition-shadow ${
        task.completed ? 'opacity-75' : ''
      }`}
      style={{ borderLeft: `4px solid ${projectColor}` }}
    >
      <div className="flex items-start gap-4">
        {/* Checkbox */}
        <button
          onClick={() => onToggleComplete(task.id)}
          className="mt-1 p-1 rounded-lg transition-transform hover:scale-110 active:scale-90 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label={task.completed ? t('tasks.completeTask') : t('tasks.completeTask')}
          type="button"
        >
          {task.completed ? (
            <SafeIcon icon={FiCheckCircle} className="text-2xl text-green-500" />
          ) : (
            <SafeIcon
              icon={FiCircle}
              className="text-2xl text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
            />
          )}
        </button>

        {/* Task Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
            <h3
              className={`text-lg font-semibold ${
                task.completed
                  ? 'text-slate-500 dark:text-slate-400 line-through'
                  : 'text-slate-800 dark:text-white'
              }`}
            >
              {task.title}
              {task.flexMode && (
                <span className="ml-2 text-xs text-purple-500">{t('tasks.flexModeActivated')}</span>
              )}
              {task.antiOverlap && (
                <span
                  className="ml-2 text-xs text-green-600 flex items-center gap-1"
                  title={t('tasks.antiOverlapTitle')}
                >
                  😊 {t('tasks.antiOverlap')}
                </span>
              )}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {/* Priority Badge */}
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getPriorityColor(
                  task.priority
                )}`}
              >
                <SafeIcon icon={FiFlag} className="text-xs" />
                {t(`taskForm.priority${task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}`)}
              </span>
            </div>
          </div>

          {task.description && (
            <div className="mb-3">
              <button
                type="button"
                onClick={() => setShowNotes(prev => !prev)}
                className="mb-2 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
              >
                <SafeIcon icon={FiFileText} className="text-xs" />
                {showNotes ? t('tasks.hideNotes') : t('tasks.showNotes')}
              </button>
              {showNotes && (
                <MarkdownContent
                  text={task.description}
                  className={`text-slate-600 dark:text-slate-400 break-words overflow-wrap-anywhere ${task.completed ? 'line-through' : ''}`}
                />
              )}
            </div>
          )}

          {/* Tags */}
          {taskTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {taskTags.map(tag => (
                <span
                  key={tag.id}
                  className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full text-xs flex items-center gap-1"
                >
                  <SafeIcon icon={FiTag} className="text-xs" />
                  {tag.name}
                </span>
              ))}
            </div>
          )}

          {taskProgress && (
            <div className="mb-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-medium mb-2">
                <span className={progressTextClass[taskProgress.tone]}>
                  {task.isMultiDay
                    ? t('tasks.multiDayProgressDay', {
                      current: taskProgress.dayLabel,
                      total: taskProgress.totalDays
                    })
                    : t('tasks.timeUnitsDueWindow')}
                </span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${progressSoftToneClass[taskProgress.tone]} ${progressTextClass[taskProgress.tone]}`}
                >
                  {tuLabel}
                  <span
                    className="inline-flex cursor-help"
                    title={tuTooltip}
                    aria-label={tuTooltip}
                  >
                    <SafeIcon icon={FiInfo} className="text-[11px]" />
                  </span>
                </span>
              </div>
              <div className="space-y-2">
                <div>
                  <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                    <span>{t('tasks.dueDateProgress')}</span>
                    <span>{taskProgress.timePercent}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                    <div
                      className="h-full rounded-full bg-slate-400 dark:bg-slate-500"
                      style={{ width: `${taskProgress.timePercent}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                    <span>{t('tasks.weightedWorkProgress')}</span>
                    <span>
                      {taskProgress.totalSubtasks > 0
                        ? t('tasks.multiDaySubtaskProgress', {
                          completed: taskProgress.completedSubtasks,
                          total: taskProgress.totalSubtasks,
                          percent: taskProgress.workPercent
                        })
                        : t('tasks.taskCompletionProgress', {
                          percent: taskProgress.workPercent
                        })}
                      {' · '}
                      <span className={progressTextClass[taskProgress.tone]}>
                        {tuSignedLabel}
                      </span>
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                    <div
                      className={`h-full rounded-full ${progressToneClass[taskProgress.tone]}`}
                      style={{ width: `${taskProgress.workPercent}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Subtasks */}
          {subtasks.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400 mb-1">
                <SafeIcon icon={FiList} className="text-xs" />
                <span>{t('tasks.subtasksCount', { completed: subtasks.filter(st => st.completed).length, total: subtasks.length })}</span>
              </div>
              <div className="pl-4 border-l-2 border-slate-200 dark:border-slate-700 space-y-1">
                <AnimatePresence initial={false}>
                  {visibleSubtasks.map(subtask => (
                    <motion.div
                      key={subtask.id}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-1 overflow-hidden"
                    >
                      <button onClick={() => onToggleComplete(subtask.id)} className="mt-0.5">
                        <SafeIcon icon={subtask.completed ? FiCheckCircle : FiCircle} className="text-xs" />
                      </button>
                      <span className={`min-w-0 break-words ${subtask.completed ? 'line-through' : ''}`}>{subtask.title}</span>
                      {task.flexMode && (
                        <span className="text-xs text-purple-500 ml-1 shrink-0">{t('tasks.flexModeActivated')}</span>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
                {subtasks.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllSubtasks(prev => !prev)}
                    className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    {showAllSubtasks
                      ? t('tasks.collapseSubtasks')
                      : t('tasks.showAllSubtasks', { count: hiddenSubtasksCount })}
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-y-2">
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
              {/* Start Date */}
              {task.dueDate && (
                <div
                  className={`flex items-center gap-1 ${
                    taskIsOverdue && !isCompleted ? 'text-red-500 dark:text-red-400' : ''
                  }`}
                >
                  <SafeIcon icon={FiCalendar} />
                  <span>
                    {task.isMultiDay ? `${t('tasks.startLabel')} ` : ''}
                    {formatDate(task.dueDate)}
                  </span>
                </div>
              )}

              {/* End Date */}
              {task.isMultiDay && task.endDate && (
                <div className="flex items-center gap-1">
                  <SafeIcon icon={FiCalendar} />
                  <span>{t('tasks.endLabel')} {formatDate(task.endDate)}</span>
                </div>
              )}

              {/* Time and Duration */}
              {!task.isMultiDay && task.startTime && (
                <div className="flex items-center gap-1">
                  <SafeIcon icon={FiClock} />
                  <span>{formatTime(task.startTime)} ({formatDuration(task.duration)})</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => onEdit(task)}
                className="p-2 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label={t('tasks.editTask')}
                title={t('tasks.editTask')}
                type="button"
              >
                <SafeIcon icon={FiEdit2} />
              </button>
              {task.completed && onArchive && (
                <button
                  onClick={() => onArchive(task.id)}
                  className="p-2 text-slate-500 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label={t('tasks.archiveTask')}
                  title={t('tasks.archiveTask')}
                  type="button"
                >
                  <SafeIcon icon={FiArchive} />
                </button>
              )}
              <button
                onClick={() => onDelete(task.id)}
                className="p-2 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label={t('tasks.deleteTask')}
                title={t('tasks.deleteTask')}
                type="button"
              >
                <SafeIcon icon={FiTrash2} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TaskItem;
